import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, ChevronDown, Upload, Loader2 } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { RefreshButton } from '@/components/ui/refresh-button';
import { getAvatarColors } from '@/lib/utils/avatar';
import { uploadLabReport, fetchBinaryAsBlob, deleteLabReport } from '@/lib/api/labReports';
import { useAuthStore } from '@/lib/stores/authStore';
import { UserRoles } from '@/lib/types/authorization';
import {
  investigationOrdersApi,
  type InvestigationOrder,
  type InvestigationOrderStatus,
} from '@/lib/api/investigation-orders';

type TabKey = 'requested' | 'accepted' | 'completed' | 'all';

// Front desk tab buttons — no "All", per their existing workflow.
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'requested', label: 'Requested' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'completed', label: 'Completed' },
];

// Practitioner's "Status" dropdown — includes "All" so they can get back to
// the combined view after filtering to one status.
const STATUS_DROPDOWN_OPTIONS: Array<{ key: TabKey; label: string }> = [
  { key: 'all', label: 'All' },
  ...TABS,
];

const ORDERS_TABLE_GRID =
  'grid-cols-[minmax(160px,1.6fr)_minmax(120px,1fr)_minmax(180px,1.8fr)_minmax(120px,1fr)_minmax(140px,1fr)_minmax(90px,0.7fr)_minmax(220px,1.6fr)]';
// "Completed" tab (and any non-manager role) is read-only — no Action column, one fewer grid track.
const ORDERS_TABLE_GRID_NO_ACTION =
  'grid-cols-[minmax(160px,1.6fr)_minmax(120px,1fr)_minmax(180px,1.8fr)_minmax(120px,1fr)_minmax(140px,1fr)_minmax(90px,0.7fr)]';

const PAGE_SIZE = 10;

function StatusPill({ status }: { status: InvestigationOrderStatus }) {
  const styles: Record<InvestigationOrderStatus, string> = {
    requested: 'bg-[#fdf1e0] text-[#b5720a]', // amber — needs attention
    accepted: 'bg-[#e6efff] text-[#2952cc]', // blue — in progress
    completed: 'bg-[#e3f8ec] text-[#0f8a4c]', // green — done
  };
  const labels: Record<InvestigationOrderStatus, string> = {
    requested: 'Requested',
    accepted: 'Accepted',
    completed: 'Completed',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 font-hanken text-[calc(14px*var(--app-scale))] font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function formatTime(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function InvestigationOrdersPage() {
  const currentUser = useAuthStore((state) => state.user);
  const currentUserName =
    currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.username;
  // Only front desk acknowledges, uploads, and marks orders completed.
  // Every other role (e.g. practitioner) can still filter by tab and view
  // reports, but sees no action buttons — fully read-only.
  const canManageOrders = currentUser?.role === UserRoles.FRONTDESK;

  // Front desk defaults to "Requested"; a practitioner lands on the
  // combined view (all statuses) since they track their patients' orders
  // overall, not just brand-new ones.
  const [activeTab, setActiveTab] = React.useState<TabKey>(canManageOrders ? 'requested' : 'all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const queryClient = useQueryClient();

  // Custom "Status" filter dropdown (practitioner view) — the trigger always
  // shows the word "Status", never the current selection, so it needs its
  // own open/close state instead of a native <select>. The menu itself is
  // rendered via a portal (see below) — the table card has overflow-x-auto
  // for horizontal scrolling, and CSS forces overflow-y to "auto" too
  // whenever overflow-x isn't "visible", which would otherwise clip the
  // dropdown instead of letting it float over the page.
  const [statusMenuOpen, setStatusMenuOpen] = React.useState(false);
  const [statusMenuPosition, setStatusMenuPosition] = React.useState({ top: 0, left: 0 });
  const statusMenuTriggerRef = React.useRef<HTMLButtonElement>(null);
  const statusMenuPortalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!statusMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        statusMenuTriggerRef.current &&
        !statusMenuTriggerRef.current.contains(target) &&
        statusMenuPortalRef.current &&
        !statusMenuPortalRef.current.contains(target)
      ) {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusMenuOpen]);

  const toggleStatusMenu = () => {
    if (!statusMenuOpen && statusMenuTriggerRef.current) {
      // The menu uses position: fixed, which is relative to the viewport —
      // don't add window.scrollY/scrollX here (that's only correct for
      // position: absolute, relative to the document).
      const rect = statusMenuTriggerRef.current.getBoundingClientRect();
      setStatusMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
    setStatusMenuOpen((open) => !open);
  };

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedQuery]);

  const {
    data: orders = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['investigation-orders', activeTab, debouncedQuery],
    queryFn: () =>
      investigationOrdersApi.list({
        status: activeTab,
        search: debouncedQuery || undefined,
      }),
    // Orders here change from other pages too (case sheet, another staff
    // member's actions) — the app's global 5-minute staleTime is too long
    // for this list; always treat it as stale so switching tabs or
    // revisiting the page shows current data instead of a cached snapshot.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: newOrders = [] } = useQuery({
    queryKey: ['investigation-orders', 'requested', 'count'],
    queryFn: () => investigationOrdersApi.list({ status: 'requested' }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  React.useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);
  const pagedOrders = orders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const invalidateOrders = () => {
    queryClient.invalidateQueries({ queryKey: ['investigation-orders'] });
  };

  const acknowledgeMutation = useMutation({
    mutationFn: (taskId: string) => investigationOrdersApi.acknowledge(taskId, currentUserName),
    onSuccess: () => {
      toast.success('Order acknowledged');
      invalidateOrders();
    },
    onError: () => toast.error('Failed to acknowledge order'),
  });

  const seenMutation = useMutation({
    mutationFn: (taskId: string) => investigationOrdersApi.markSeen(taskId, currentUserName),
    onSuccess: () => {
      toast.success('Order marked as seen');
      invalidateOrders();
    },
    onError: () => toast.error('Failed to mark order as seen'),
  });

  const cancelMutation = useMutation({
    mutationFn: (serviceRequestId: string) => investigationOrdersApi.cancel(serviceRequestId),
    onSuccess: () => {
      toast.success('Order cancelled');
      invalidateOrders();
    },
    onError: () => toast.error('Failed to cancel order'),
  });

  // --- Report upload / view, directly from this page ---
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = React.useState<InvestigationOrder | null>(null);
  const [viewingReportId, setViewingReportId] = React.useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (input: { file: File; order: InvestigationOrder }) => {
      const { file, order } = input;
      if (!order.patientId) {
        throw new Error('Missing patient information for this order.');
      }
      // Replacing a wrongly uploaded file: upload the new one first, then
      // remove the old one — if the upload fails, the existing report is
      // left intact instead of being lost.
      const uploaded = await uploadLabReport(
        file,
        order.patientId,
        order.encounterId,
        undefined,
        order.serviceRequestId
      );
      if (order.report) {
        await deleteLabReport(order.report.id);
      }
      return uploaded;
    },
    onSuccess: (_data, variables) => {
      toast.success(variables.order.report ? 'Report replaced' : 'Report uploaded');
      invalidateOrders();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to upload report'),
  });

  const handleUploadClick = (order: InvestigationOrder) => {
    setUploadTarget(order);
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadTarget) return;
    uploadMutation.mutate({ file, order: uploadTarget });
    setUploadTarget(null);
  };

  const handleViewReport = async (order: InvestigationOrder) => {
    const report = order.report;
    if (!report?.binaryRef?.startsWith('Binary/')) {
      toast.error('Report file could not be found');
      return;
    }
    const binaryId = report.binaryRef.replace('Binary/', '');
    setViewingReportId(order.serviceRequestId);
    try {
      const blobUrl = await fetchBinaryAsBlob(
        binaryId,
        report.id,
        report.contentType || 'application/octet-stream'
      );
      window.open(blobUrl, '_blank', 'noopener');
    } catch {
      toast.error('Failed to open report');
    } finally {
      setViewingReportId(null);
    }
  };

  // Action buttons (Accept/Complete/Edit/Cancel/Upload) are front
  // desk-only, and only make sense on active orders — "Completed" is
  // read-only history at this point.
  const showActionColumn =
    canManageOrders && (activeTab === 'requested' || activeTab === 'accepted');

  return (
    <AppLayout>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.tiff,.webp,.bmp"
        className="hidden"
        onChange={handleFileSelected}
      />
      <div className="relative flex w-full flex-col bg-[#e1e3ed]">
        <div className="flex flex-col py-6">
          {/* Header Section - Title, Tabs, Search */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="flex-shrink-0 font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))] tracking-normal text-gray-700">
              Investigation Orders
            </h1>

            <div className="flex items-center gap-3">
              {canManageOrders && (
                <div className="flex gap-2">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`rounded-full px-4 py-2 font-hanken text-[calc(14px*var(--app-scale))] font-medium transition-colors ${
                        activeTab === tab.key
                          ? 'bg-[#061a61] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                      {tab.key === 'requested' && newOrders.length > 0 && ` (${newOrders.length})`}
                    </button>
                  ))}
                </div>
              )}

              <div className="relative w-[calc(240px*var(--app-scale))]">
                <Search className="absolute left-3 top-1/2 h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] -translate-y-1/2 text-gray-700" />
                <input
                  type="text"
                  placeholder="Search patient"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-full bg-white py-2 pl-10 pr-4 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700 shadow-sm placeholder:text-[#bcc0d7] focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
                />
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="mt-4 flex flex-col overflow-hidden">
            <div className="relative flex flex-col overflow-x-auto rounded-3xl bg-white shadow-sm">
              <RefreshButton
                onClick={() => refetch()}
                isLoading={isLoading || isFetching}
                className="absolute right-4 top-3"
              />
              <div
                className={`flex flex-col ${showActionColumn ? 'min-w-[1040px]' : 'min-w-[820px]'}`}
              >
                {/* Table Header — Action column only shown to front desk on active tabs */}
                <div
                  className={`grid flex-shrink-0 ${showActionColumn ? ORDERS_TABLE_GRID : ORDERS_TABLE_GRID_NO_ACTION} items-center gap-[2px] border-b border-gray-200 px-4 py-3`}
                >
                  {(showActionColumn
                    ? [
                        'Patient',
                        'Ordered by',
                        'Investigations',
                        'Ordered on',
                        'Status',
                        'Report',
                        'Action',
                      ]
                    : ['Patient', 'Ordered by', 'Investigations', 'Ordered on', 'Status', 'Report']
                  ).map((label) =>
                    label === 'Status' && !canManageOrders ? (
                      // Practitioners have no top tab bar — the same status
                      // filter lives here instead, right in the column header.
                      // The trigger always reads "Status", never the current
                      // selection — a native <select> can't do that, so this
                      // is a small custom dropdown instead.
                      <div key={label} className="relative flex justify-center">
                        <button
                          ref={statusMenuTriggerRef}
                          type="button"
                          onClick={toggleStatusMenu}
                          className="flex items-center gap-1 font-hanken text-[calc(18px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61] focus:outline-none"
                        >
                          Status
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        {statusMenuOpen &&
                          createPortal(
                            <div
                              ref={statusMenuPortalRef}
                              style={{
                                top: statusMenuPosition.top,
                                left: statusMenuPosition.left,
                                backgroundColor: 'rgb(255, 255, 255)',
                                opacity: 1,
                                isolation: 'isolate',
                                zIndex: 9999,
                              }}
                              className="fixed w-36 rounded-md border-2 border-gray-300 py-1 shadow-xl"
                            >
                              {STATUS_DROPDOWN_OPTIONS.map((tab) => (
                                <button
                                  key={tab.key}
                                  type="button"
                                  onClick={() => {
                                    setActiveTab(tab.key);
                                    setStatusMenuOpen(false);
                                  }}
                                  className={`block w-full px-3 py-2 text-left font-hanken text-[calc(14px*var(--app-scale))] ${
                                    activeTab === tab.key
                                      ? 'bg-[#061a61] text-white'
                                      : 'bg-white text-[#061A61] hover:bg-gray-50'
                                  }`}
                                >
                                  {tab.label}
                                </button>
                              ))}
                            </div>,
                            document.body
                          )}
                      </div>
                    ) : (
                      <div key={label} className="text-center">
                        <p className="font-hanken text-[calc(18px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61]">
                          {label}
                        </p>
                      </div>
                    )
                  )}
                </div>

                {/* Table Body */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="h-[calc(32px*var(--app-scale))] w-[calc(32px*var(--app-scale))] animate-spin rounded-full border-4 border-[#6b8af9] border-t-transparent" />
                  </div>
                ) : isError ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <p className="font-hanken text-[calc(16px*var(--app-scale))] text-red-500">
                      Failed to load investigation orders. Please try again.
                    </p>
                    <button
                      onClick={() => refetch()}
                      className="mt-4 rounded-md bg-[#6B8AF9] px-4 py-2 text-[calc(16px*var(--app-scale))] text-white hover:bg-[#566EC7]"
                    >
                      Retry
                    </button>
                  </div>
                ) : pagedOrders.length === 0 ? (
                  <div className="flex items-center justify-center py-20">
                    <p className="font-hanken text-[calc(16px*var(--app-scale))] text-gray-700">
                      {searchQuery
                        ? 'No investigation orders found matching your search.'
                        : 'No investigation orders found.'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pagedOrders.map((order) => {
                      const colors = getAvatarColors(order.patientName || '?');
                      return (
                        <div
                          key={order.taskId}
                          className={`grid ${showActionColumn ? ORDERS_TABLE_GRID : ORDERS_TABLE_GRID_NO_ACTION} items-center gap-[2px] px-4 py-2 hover:bg-gray-50`}
                        >
                          {/* Patient */}
                          <div className="flex items-center gap-3">
                            <div
                              className="flex h-[calc(44px*var(--app-scale))] w-[calc(44px*var(--app-scale))] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/50"
                              style={{ backgroundColor: colors.bg }}
                            >
                              <span
                                className="font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))]"
                                style={{ color: colors.text }}
                              >
                                {(order.patientName || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <p className="truncate font-hanken text-[calc(18px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                              {order.patientName || 'Unknown patient'}
                            </p>
                          </div>

                          {/* Ordered by */}
                          <div className="text-center">
                            <p className="font-hanken text-[calc(18px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                              {order.practitionerName || '—'}
                            </p>
                          </div>

                          {/* Investigations */}
                          <div className="text-center">
                            <p className="font-hanken text-[calc(18px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                              {order.investigations || '—'}
                            </p>
                          </div>

                          {/* Ordered on */}
                          <div className="text-center">
                            <p className="font-hanken text-[calc(18px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                              {formatDate(order.authoredOn)}
                            </p>
                          </div>

                          {/* Status */}
                          <div className="flex flex-col items-center justify-center gap-0.5">
                            <StatusPill status={order.status} />
                            {order.status === 'completed' && order.seenBy && (
                              <p className="font-hanken text-[calc(13px*var(--app-scale))] text-gray-500">
                                {order.seenBy}
                                {order.seenAt ? ` · ${formatTime(order.seenAt)}` : ''}
                              </p>
                            )}
                            {order.status === 'accepted' && order.acknowledgedBy && (
                              <p className="font-hanken text-[calc(13px*var(--app-scale))] text-gray-500">
                                {order.acknowledgedBy}
                                {order.acknowledgedAt
                                  ? ` · ${formatTime(order.acknowledgedAt)}`
                                  : ''}
                              </p>
                            )}
                          </div>

                          {/* Report */}
                          <div className="flex items-center justify-center gap-2">
                            {order.hasReport ? (
                              <button
                                onClick={() => handleViewReport(order)}
                                disabled={viewingReportId === order.serviceRequestId}
                                className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-[#6b8af9] hover:underline disabled:opacity-50"
                              >
                                {viewingReportId === order.serviceRequestId ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'View Report'
                                )}
                              </button>
                            ) : showActionColumn ? (
                              <button
                                onClick={() => handleUploadClick(order)}
                                disabled={uploadMutation.isPending}
                                className="flex items-center gap-1 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-500 hover:text-[#6b8af9] disabled:opacity-50"
                              >
                                <Upload className="h-4 w-4" />
                                Upload
                              </button>
                            ) : (
                              <span className="font-hanken text-[calc(16px*var(--app-scale))] text-gray-400">
                                No Report
                              </span>
                            )}
                          </div>

                          {/* Action column — front desk only, on active tabs */}
                          {showActionColumn && (
                            <div className="flex flex-shrink-0 items-center justify-center gap-2">
                              {order.status === 'requested' && (
                                <button
                                  onClick={() => acknowledgeMutation.mutate(order.taskId)}
                                  disabled={acknowledgeMutation.isPending}
                                  className="flex h-[calc(36px*var(--app-scale))] items-center justify-center whitespace-nowrap rounded-lg bg-[#061a61] px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-white hover:bg-[#0a2480] disabled:opacity-50"
                                >
                                  Accept
                                </button>
                              )}
                              {order.status === 'accepted' && (
                                <button
                                  onClick={() => seenMutation.mutate(order.taskId)}
                                  disabled={seenMutation.isPending}
                                  className="flex h-[calc(36px*var(--app-scale))] items-center justify-center whitespace-nowrap rounded-lg bg-[#061a61] px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-white hover:bg-[#0a2480] disabled:opacity-50"
                                >
                                  Complete
                                </button>
                              )}
                              {order.hasReport &&
                                (order.status === 'requested' || order.status === 'accepted') && (
                                  <button
                                    onClick={() => handleUploadClick(order)}
                                    disabled={uploadMutation.isPending}
                                    title="Replace report — undo an accidental upload"
                                    className="flex h-[calc(36px*var(--app-scale))] items-center whitespace-nowrap rounded-lg border border-[#eaebf3] bg-white px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                  >
                                    Edit Report
                                  </button>
                                )}
                              {order.status !== 'completed' && (
                                <button
                                  onClick={() => cancelMutation.mutate(order.serviceRequestId)}
                                  disabled={cancelMutation.isPending}
                                  className="flex h-[calc(36px*var(--app-scale))] items-center justify-center whitespace-nowrap rounded-lg border border-[#eaebf3] bg-white px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pagination - Fixed at bottom right */}
          {!isLoading && orders.length > 0 && (
            <div className="fixed bottom-4 right-6">
              <div className="flex items-center gap-4 rounded-full bg-[#e6e8ef] px-4 py-2 shadow-md">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
                >
                  <ChevronLeft className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
                </button>
                <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-[#42434b]">
                  {page} out of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
                >
                  <ChevronRight className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
